import { Error as ErrorMessage } from "@convergence/components";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { authClient, redirectUrl } from "../auth-client";
import { Header } from "./LoginOtp";

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  mcp: "Use Tinker tools on your behalf, including running commands on your boxes",
  openid: "Confirm who you are",
  profile: "See your name",
  email: "See your email address",
  offline_access: "Stay connected until you revoke access",
};

// Shown by the API's OAuth authorize flow when an app (e.g. ChatGPT) asks to connect.
export function Consent() {
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("client_id") ?? "";
  const scopes = (searchParams.get("scope") ?? "").split(" ").filter(Boolean);

  const client = useQuery({
    queryKey: ["oauthClient", clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await authClient.$fetch<{ client_name?: string }>(
        "/oauth2/public-client",
        { method: "GET", query: { client_id: clientId } },
      );
      if (error) throw new Error(error.message ?? "Unknown application");
      return data;
    },
  });

  const consent = useMutation({
    mutationFn: async (accept: boolean) => {
      const { data, error } = await authClient.oauth2.consent({ accept });
      if (error) throw new Error(error.message ?? "Unable to save your choice");
      return data;
    },
    onSuccess: (data) => {
      const url = redirectUrl(data);
      if (url) window.location.href = url;
    },
  });

  const appName = client.data?.client_name ?? "An application";

  return (
    <div className="w-screen h-screen flex flex-col font-body">
      <Header />
      <main className="w-full bg-white h-full flex-grow grid place-items-center">
        <section className="bg-white border-black border p-8 flex flex-col gap-4 max-w-md">
          <h2 className="text-3xl">Connect {appName} to Tinker</h2>
          <p>{appName} is asking to:</p>
          <ul className="list-disc pl-5 space-y-1">
            {scopes.map((scope) => (
              <li key={scope}>{SCOPE_DESCRIPTIONS[scope] ?? scope}</li>
            ))}
          </ul>
          {client.error && <ErrorMessage>{client.error.message}</ErrorMessage>}
          {consent.error && <ErrorMessage>{consent.error.message}</ErrorMessage>}
          <div className="flex gap-4">
            <button
              className="bg-black text-white px-4 py-2 rounded-none disabled:opacity-50"
              disabled={consent.isPending}
              onClick={() => consent.mutate(true)}
            >
              Allow
            </button>
            <button
              className="border border-black px-4 py-2 rounded-none disabled:opacity-50"
              disabled={consent.isPending}
              onClick={() => consent.mutate(false)}
            >
              Deny
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
